import React, { useState } from "react";
import { useCursor } from "../providers/CursorContext";

interface RippleButtonProps {
  text?: string;
  onTap?: () => void;
  color?: string; // CSS bg color class e.g., 'bg-amber-400'
  hoverColor?: string;
  rippleColor?: string;
  textColor?: string;
  child?: React.ReactNode;
  children?: React.ReactNode;
  padding?: string; // CSS padding class e.g., 'p-4'
  border?: string; // CSS border class e.g., 'border border-amber-500'
  radius?: string; // CSS border-radius class e.g., 'rounded-md'
  enableShadow?: boolean;
  onHover?: (hovered: boolean) => void;
  className?: string;
}

export const RippleButton: React.FC<RippleButtonProps> = ({
  text,
  onTap,
  color = "bg-primary", // Themes.primary
  hoverColor,
  textColor,
  child,
  children,
  padding = "p-4",
  border = "",
  radius = "rounded-[6px]",
  enableShadow = false,
  onHover,
  className = "",
}) => {
  const { setCursorSize, setHover } = useCursor();
  const [isPressing, setIsPressing] = useState(false);
  const finalTextColor = textColor || (color.includes("bg-primary") ? "text-white dark:text-neutral-900" : "text-white");

  const handleMouseEnter = () => {
    setHover(true);
    setCursorSize(48);
    onHover?.(true);
  };

  const handleMouseLeave = () => {
    setHover(false);
    setCursorSize(24);
    onHover?.(false);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (!onTap) return;
    setIsPressing(true);
    
    // Quick scale feedback
    setTimeout(() => {
      setIsPressing(false);
    }, 100);

    // Bouncing cursor size feedback like Flutter
    setCursorSize(24);
    setTimeout(() => {
      setCursorSize(48);
    }, 100);

    onTap();
  };

  const baseStyle = onTap ? color : "bg-stroke cursor-not-allowed";
  const shadowStyle = "";

  const renderContent = children || child;

  return (
    <button
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`relative overflow-hidden transition-all duration-100 ease-out active:scale-95 hover:scale-[0.97] ${baseStyle} ${border} ${radius} ${shadowStyle} ${className}`}
      style={{
        outline: "none",
      }}
    >
      <div className={`${padding} flex items-center justify-center`}>
        {renderContent ? (
          renderContent
        ) : (
          <span className={`text-sm font-semibold tracking-wide ${finalTextColor}`}>
            {text!}
          </span>
        )}
      </div>
    </button>
  );
};
